/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { OneLineGuideCell } from '../../../core/entities';
import { Channel } from '../../../core/entities/Channel';
import { InteractiveComponents } from '../../../core/enums';
import { GuideError } from '../../../core/error';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TChannelV2, TOfferPreview } from '../../../core/types';
import { DateTime } from '../../../core/utils';
import { TOLGInitialState } from './onelineguide.props';
import {
    SET_ONE_LINE_GUIDE_ACTIVE,
    SET_ONE_LINE_GUIDE_ARE_CHANNELS_SELECTED,
    SET_ONE_LINE_GUIDE_ARE_FUTURE_CELLS_RECEIVED,
    SET_ONE_LINE_GUIDE_ARE_PAST_CELLS_RECEIVED,
    SET_ONE_LINE_GUIDE_CELL_AIRING_NOW,
    SET_ONE_LINE_GUIDE_CELL_SELECTED,
    SET_ONE_LINE_GUIDE_CELLS_APPEND,
    SET_ONE_LINE_GUIDE_CELLS_DELETE,
    SET_ONE_LINE_GUIDE_CELLS_PREPEND,
    SET_ONE_LINE_GUIDE_CHANNELS,
    SET_ONE_LINE_GUIDE_CURRENT_OFFER_PREVIEW,
    SET_ONE_LINE_GUIDE_CURRENT_SELECTED_CELL_STAR_RATING,
    SET_ONE_LINE_GUIDE_CURRENTLY_AIRING_CHANNEL,
    SET_ONE_LINE_GUIDE_EMPTY,
    SET_ONE_LINE_GUIDE_ERROR,
    SET_ONE_LINE_GUIDE_HIDDEN,
    SET_ONE_LINE_GUIDE_HORIZONTAL_SCROLL,
    SET_ONE_LINE_GUIDE_INACTIVE,
    SET_ONE_LINE_GUIDE_IS_CONTENT_FETCHED,
    SET_ONE_LINE_GUIDE_IS_FUTURE_MODE,
    SET_ONE_LINE_GUIDE_SELECTED_CHANNEL,
    SET_ONE_LINE_GUIDE_TIME_STAMP,
    SET_ONE_LINE_GUIDE_TURBO_SCROLL,
    UPDATE_ONE_LINE_GUIDE_AIRING_NOW_TIMEOUT,
    UPDATE_ONE_LINE_GUIDE_INACTIVITY_TIMEOUT,
    UPDATE_ONE_LINE_GUIDE_STICKINESS_TIMEOUT,
} from './onelineguide.types';

export const setOneLineGuideActiveAction = (): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_ACTIVE,
    payload: {},
});

export const setOneLineGuideCellsAppendAction = (
    cellList: OneLineGuideCell[],
    isPastMode: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: isPastMode ? SET_ONE_LINE_GUIDE_CELLS_PREPEND : SET_ONE_LINE_GUIDE_CELLS_APPEND,
    payload: { cellList },
});

export const setOneLineGuideCellAiringNowAction = (
    currentAiringCell: OneLineGuideCell,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_CELL_AIRING_NOW,
    payload: { currentAiringCell },
});

export const setOneLineGuideCellSelectedAction = (
    currentSelectedCell: OneLineGuideCell,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_CELL_SELECTED,
    payload: { currentSelectedCell },
});

export const setOneLineGuideCurrentSelectedCellStarRatingAction = (
    currentSelectedCellStarRating: number | null,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_CURRENT_SELECTED_CELL_STAR_RATING,
    payload: { currentSelectedCellStarRating },
});

export const setOneLineGuideCurrentlyAiringChannelAction = (
    currentlyAiringChannel: TChannelV2 | Channel,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_CURRENTLY_AIRING_CHANNEL,
    payload: { currentlyAiringChannel },
});

export const setOneLineGuideChannelsAction = (
    channels: TChannelV2[] | Channel[],
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_CHANNELS,
    payload: { channels },
});

export const setOneLineGuideInactiveAction = (): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_INACTIVE,
    payload: {},
});

export const setOneLineGuideCellsDeleteAction = (): TivoPayloadAction<
    Partial<TOLGInitialState>
> => ({
    type: SET_ONE_LINE_GUIDE_CELLS_DELETE,
    payload: {},
});

export const setOneLineGuideAreChannelsSelectedAction = (
    areChannelsSelected: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_ARE_CHANNELS_SELECTED,
    payload: { areChannelsSelected },
});

export const setOneLineGuideEmptyAction = (
    isEmpty: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_EMPTY,
    payload: { isEmpty },
});

export const setOneLineGuideError = (
    error: GuideError | null,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_ERROR,
    payload: { error },
});

export const setOneLineGuideIsContentFetched = (
    isContentFetched: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_IS_CONTENT_FETCHED,
    payload: { isContentFetched },
});

export const setOneLineGuideIsFutureModeAction = (
    isFutureMode: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_IS_FUTURE_MODE,
    payload: { isFutureMode },
});

export const setOneLineGuideHidden = (): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_HIDDEN,
    payload: {},
});

export const setOneLineGuideSelectedChannelAction = (
    selectedChannel: TChannelV2 | Channel,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_SELECTED_CHANNEL,
    payload: { selectedChannel },
});

export const setOneLineGuideCurrentOfferPreviewAction = (
    offerPreview: Promise<TOfferPreview>,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_CURRENT_OFFER_PREVIEW,
    payload: { offerPreview },
});

export const setOneLineGuideTimeStamp = (
    timeStamp: DateTime,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_TIME_STAMP,
    payload: { timeStamp },
});

export const setOneLineGuideAreFutureCellsReceived = (
    areFutureCellsReceived: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_ARE_FUTURE_CELLS_RECEIVED,
    payload: { areFutureCellsReceived },
});

export const setOneLineGuideArePastCellsReceived = (
    arePastCellsReceived: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_ARE_PAST_CELLS_RECEIVED,
    payload: { arePastCellsReceived },
});
export const setOneLineGuideArePastCellsReceivedAction = (
    arePastCellsReceived: boolean,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_ARE_PAST_CELLS_RECEIVED,
    payload: { arePastCellsReceived },
});

export const updateOneLineGuideAiringNowTimeoutAction = (
    timeout: NodeJS.Timeout,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: UPDATE_ONE_LINE_GUIDE_AIRING_NOW_TIMEOUT,
    payload: { oneLineGuideAiringNowTimeout: timeout },
});

export const updateOneLineGuideInactivityTimeoutAction = (
    timeout: NodeJS.Timeout,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: UPDATE_ONE_LINE_GUIDE_INACTIVITY_TIMEOUT,
    payload: { oneLineGuideInactivityTimeout: timeout },
});

export const updateOneLineGuideStickinessTimeoutAction = (
    timeout: NodeJS.Timeout,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: UPDATE_ONE_LINE_GUIDE_STICKINESS_TIMEOUT,
    payload: { oneLineGuideStickinessTimeout: timeout },
});

export const setOneLineGuideTurboScrollAction = (
    value: boolean,
    direction?: InteractiveComponents.NORTH | InteractiveComponents.SOUTH,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_TURBO_SCROLL,
    payload: { oneLineGuideTurboScroll: value, oneLineGuideTurboScrollDirection: direction },
});

export const setOneLineGuideHorizontalScrollAction = (
    direction?: null | InteractiveComponents.EAST | InteractiveComponents.WEST,
): TivoPayloadAction<Partial<TOLGInitialState>> => ({
    type: SET_ONE_LINE_GUIDE_HORIZONTAL_SCROLL,
    payload: { oneLineGuideHorizontalDirection: direction },
});
